import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TemplateMeta } from '../../api/templates';
import { usePublicTemplates, type TemplateCategory } from '../../hooks/usePublicTemplates';
import { TemplateGalleryCard } from './TemplateGalleryCard';
import { TemplateDetailPanel } from './TemplateDetailPanel';
import { TEMPLATE_DETAIL_ID, scrollToDigitalCardSection } from './digitalCardRoutes';

type Props = {
  category?: TemplateCategory | 'all';
  limit?: number;
  showExplore?: boolean;
  enableDetailPanel?: boolean;
  browseOnly?: boolean;
  layout?: 'default' | 'linktree';
  title?: string;
  subtitle?: string;
};

export const TemplatesGallerySection: React.FC<Props> = ({
  category = 'all',
  limit,
  showExplore = true,
  enableDetailPanel = false,
  browseOnly = false,
  layout = 'default',
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { templates, loading, error } = usePublicTemplates(category);

  const scrollToDetail = useCallback(() => {
    window.setTimeout(() => scrollToDigitalCardSection(TEMPLATE_DETAIL_ID), 50);
  }, []);

  const handleTemplateSelect = useCallback(
    (item: TemplateMeta) => {
      setSelectedId(item.id);
      scrollToDetail();
    },
    [scrollToDetail],
  );

  const visible = useMemo(() => {
    return typeof limit === 'number' ? templates.slice(0, limit) : templates;
  }, [templates, limit]);

  const selectedTemplate = useMemo(
    () => visible.find((tpl) => tpl.id === selectedId) || null,
    [visible, selectedId]
  );

  useEffect(() => {
    setSelectedId(null);
  }, [category]);

  useEffect(() => {
    if (!enableDetailPanel) return;
    if (!visible.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !visible.some((tpl) => tpl.id === selectedId)) {
      setSelectedId(visible[0].id);
    }
  }, [enableDetailPanel, visible, selectedId]);

  const cardVariant = layout === 'linktree' ? 'linktree' : 'default';

  const grid = (
    <div className={layout === 'linktree' ? 'dcl-lt-template-grid' : 'dcl-template-grid'}>
      {visible.map((tpl) => (
        <TemplateGalleryCard
          key={tpl.id}
          template={tpl}
          variant={cardVariant}
          selected={enableDetailPanel && selectedId === tpl.id}
          onSelect={enableDetailPanel ? handleTemplateSelect : undefined}
          showActions={!browseOnly && layout !== 'linktree'}
        />
      ))}
    </div>
  );

  return (
    <div className={layout === 'linktree' ? 'dcl-lt-gallery' : undefined}>
      {(title || subtitle) && (
        <div style={{ marginBottom: 28 }}>
          {title && <h2 className="dcl-section-title">{title}</h2>}
          {subtitle && <p className="dcl-section-sub">{subtitle}</p>}
        </div>
      )}

      {loading && (
        <p className="dcl-lt-status">{t('dcl_templates_loading', { defaultValue: 'Loading templates…' })}</p>
      )}
      {error && !loading && <p className="dcl-lt-status dcl-lt-status--err">{error}</p>}
      {!loading && visible.length === 0 && (
        <p className="dcl-lt-status">{t('dcl_templates_empty', { defaultValue: 'No templates available in this category yet.' })}</p>
      )}

      {!loading && visible.length > 0 && grid}

      {enableDetailPanel && selectedTemplate && (
        <div
          id={TEMPLATE_DETAIL_ID}
          className={`dcl-scroll-target${layout === 'linktree' ? ' dcl-lt-detail-wrap' : ''}`}
        >
          <TemplateDetailPanel template={selectedTemplate} layout={layout === 'linktree' ? 'linktree' : 'default'} />
        </div>
      )}

      {showExplore && (
        <div style={{ textAlign: 'center', marginTop: 36 }}>
          <Link to="/templates" className="dcl-btn dcl-btn--secondary">
            {t('dcl_explore_all_templates', { defaultValue: 'Explore All Templates' })}
          </Link>
        </div>
      )}
    </div>
  );
};

export default TemplatesGallerySection;
